import React, { useState } from 'react';
import { Project, User } from '../../../types';

interface ProjectTeamProps {
  project: Project;
  canEdit: boolean;
  allUsers: User[];
  onAddMember: (userId: string) => Promise<void>;
  onRemoveMember: (memberId: string) => Promise<void>;
}

const ProjectTeam: React.FC<ProjectTeamProps> = ({ project, canEdit, allUsers, onAddMember, onRemoveMember }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [memberSearchTerm, setMemberSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);

  const toggleUser = (id: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Filter out users who are already members or owner
  const existingMemberIds = [
    project.owner.userId,
    ...project.members.map(m => m.userId)
  ];
  const availableUsers = allUsers.filter(u => u._id && !existingMemberIds.includes(u._id) && u.role !== 'admin');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUserIds.size === 0) return;

    setSubmitting(true);
    try {
      for (const uid of selectedUserIds) {
        await onAddMember(uid);
      }
      setSelectedUserIds(new Set());
      setMemberSearchTerm('');
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding member:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    setRemovingMemberId(memberId);
    try {
      await onRemoveMember(memberId);
    } catch (err) {
      console.error('Error removing member:', err);
    } finally {
      setRemovingMemberId(null);
    }
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-700">Team Members</h3>
        {canEdit && !showAddForm && availableUsers.length > 0 && (
          <button
            onClick={() => setShowAddForm(true)}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            + Add member
          </button>
        )}
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit} className="mb-3 bg-gray-50 rounded-lg p-4">
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select User to Add
              </label>
              {/* Search Field */}
              <div className="mb-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={memberSearchTerm}
                    onChange={(e) => setMemberSearchTerm(e.target.value)}
                    className="w-full px-3 py-2 pl-9 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <svg
                    className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>
              {/* User List */}
              <div className="border border-gray-300 rounded-lg p-3 max-h-60 overflow-y-auto bg-white">
                {(() => {
                  const filteredUsers = availableUsers.filter((user: User) => {
                    if (!memberSearchTerm) return true;
                    const searchLower = memberSearchTerm.toLowerCase();
                    return (
                      user.name?.toLowerCase().includes(searchLower) ||
                      user.email?.toLowerCase().includes(searchLower)
                    );
                  });

                  if (filteredUsers.length === 0) {
                    return (
                      <div className="text-center py-6 text-gray-500">
                        <svg className="w-10 h-10 mx-auto mb-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <p className="text-sm">
                          {memberSearchTerm ? `No users found matching "${memberSearchTerm}"` : 'No available users to add'}
                        </p>
                        {memberSearchTerm && <p className="text-xs mt-1">Try a different search term</p>}
                      </div>
                    );
                  }

                  return filteredUsers.map((user) => (
                    <label
                      key={user._id}
                      className={`flex items-center gap-3 py-2 px-2 hover:bg-gray-50 rounded cursor-pointer transition-colors ${selectedUserIds.has(user._id!) ? 'bg-blue-50 border border-blue-200' : ''}`}
                    >
                      <input
                        type="checkbox"
                        value={user._id}
                        checked={selectedUserIds.has(user._id!)}
                        onChange={() => toggleUser(user._id!)}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>
                    </label>
                  ));
                })()}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {selectedUserIds.size > 0
                  ? <span className="text-blue-600 font-medium">{selectedUserIds.size} selected</span>
                  : memberSearchTerm
                    ? <span>Showing {availableUsers.filter((user: User) => {
                        const searchLower = memberSearchTerm.toLowerCase();
                        return user.name?.toLowerCase().includes(searchLower) || user.email?.toLowerCase().includes(searchLower);
                      }).length} of {availableUsers.length} users</span>
                    : <span>{availableUsers.length} available users</span>
                }
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={submitting || selectedUserIds.size === 0}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {submitting ? 'Adding…' : selectedUserIds.size > 1 ? `Add ${selectedUserIds.size} Members` : 'Add Member'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setSelectedUserIds(new Set());
                  setMemberSearchTerm('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Project owner/creator — always shown, cannot be removed */}
      <div className="space-y-2">
        <div className="flex items-center gap-3 p-2 bg-blue-50 rounded">
          <div className="w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {project.owner.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{project.owner.name}</p>
            <p className="text-xs text-blue-600">Owner</p>
          </div>
        </div>

        {/* Team members */}
        {project.members.map((member, index) => (
          <div key={index} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {member.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{member.name}</p>
              <p className="text-xs text-gray-500">{member.role}</p>
            </div>
            {canEdit && (
              <button
                onClick={() => handleRemoveMember(member.userId)}
                disabled={removingMemberId === member.userId}
                className="text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
              >
                {removingMemberId === member.userId ? 'Removing...' : 'Remove'}
              </button>
            )}
          </div>
        ))}
      </div>

      {project.members.length === 0 && !showAddForm && (
        <p className="text-sm text-gray-500 mt-2">No additional team members yet</p>
      )}
    </div>
  );
};

export default ProjectTeam;
